import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, titleCase } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Target, Loader2 } from "lucide-react";
import { toast } from "sonner";

const thisMonth = () => new Date(Date.now() + 5.5 * 3600e3).toISOString().slice(0, 7);
const pct = (d, t) => (t > 0 ? Math.min(100, Math.round((d / t) * 100)) : 0);
const overall = (segs) => { const parts = (segs || []).flatMap((s) => [s.amount > 0 && Math.min(1, s.done_amount / s.amount), s.count > 0 && Math.min(1, s.done_count / s.count)]).filter((x) => x !== false);
  return parts.length ? Math.round((parts.reduce((a, b) => a + b, 0) / parts.length) * 100) : null; };
const MEDAL = ["bg-amber-400 text-amber-950", "bg-slate-300 text-slate-800", "bg-orange-300 text-orange-950"];

function Leaderboard({ people, data }) {
  const ranked = people.map((p) => ({ ...p, score: overall(data[p.id]) })).filter((p) => p.score !== null).sort((a, b) => b.score - a.score);
  if (!ranked.length) return null;
  return (
    <Card className="p-4 mb-4" data-testid="target-leaderboard">
      <div className="font-bold text-sm mb-2">Leaderboard · % of target reached</div>
      <div className="space-y-1.5">{ranked.map((p, i) => (
        <div key={p.id} className="flex items-center gap-3 text-sm" data-testid="leaderboard-row">
          <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${MEDAL[i] || "bg-muted"}`}>{i + 1}</span>
          <span className="flex-1 truncate">{p.name} <span className="text-xs text-muted-foreground font-mono">{p.employee_id}</span></span>
          <div className="w-28 h-1.5 rounded-full bg-muted overflow-hidden hidden sm:block"><div className="h-full bg-primary rounded-full" style={{ width: `${p.score}%` }} /></div>
          <b className="tabular w-12 text-right">{p.score}%</b>
        </div>))}</div>
    </Card>
  );
}

function Bar({ label, done, target, money }) {
  if (!target) return null;
  const p = pct(done, target);
  return (
    <div className="text-xs">
      <div className="flex justify-between"><span>{label}</span><span className="tabular">{money ? `${INR(done)} / ${INR(target)}` : `${done} / ${target}`} · <b>{p}%</b></span></div>
      <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-500 ${p >= 100 ? "bg-emerald-500" : p >= 60 ? "bg-primary" : "bg-amber-500"}`} style={{ width: `${p}%` }} /></div>
    </div>
  );
}

function SetTargets({ person, month, segments, current, onClose, onSaved }) {
  const init = {};
  for (const s of current || []) init[`${s.business}|${s.segment}`] = { amount: s.amount || "", count: s.count || "" };
  const [v, setV] = useState(init);
  const [busy, setBusy] = useState(false);
  const set = (k, f, val) => setV({ ...v, [k]: { ...(v[k] || {}), [f]: val } });
  const save = async () => {
    setBusy(true);
    const rows = Object.entries(v).map(([k, x]) => { const [business, segment] = k.split("|"); return { business, segment, amount: Number(x.amount || 0), count: Number(x.count || 0) }; });
    try { await api.put(`/sales-targets/${person.id}`, { month, segments: rows }); toast.success("Targets saved"); onSaved(); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-xl max-h-[85vh] overflow-y-auto rounded-xl" data-testid="set-targets-dialog">
        <DialogHeader><DialogTitle>Targets · {person.name} · {month}</DialogTitle></DialogHeader>
        {Object.entries(segments).map(([biz, list]) => (
          <div key={biz}><div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-2 mb-1">{biz === "loan" ? "Loans (disbursed amount)" : "Insurance (premium)"}</div>
            {list.map((seg) => { const k = `${biz}|${seg}`; return (
              <div key={k} className="grid grid-cols-[1fr_7rem_5rem] gap-2 items-center py-1">
                <span className="text-sm">{seg}</span>
                <Input type="number" min="0" placeholder="₹ amount" value={v[k]?.amount ?? ""} onChange={(e) => set(k, "amount", e.target.value)} data-testid={`target-amount-${biz}-${seg.replace(/\s+/g, "-").toLowerCase()}`} />
                <Input type="number" min="0" placeholder="count" value={v[k]?.count ?? ""} onChange={(e) => set(k, "count", e.target.value)} data-testid={`target-count-${biz}-${seg.replace(/\s+/g, "-").toLowerCase()}`} />
              </div>); })}
          </div>))}
        <Button onClick={save} disabled={busy} data-testid="set-targets-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save targets"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export function TargetsPanel() {
  const { user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [people, setPeople] = useState([]);
  const [data, setData] = useState(null);
  const [segments, setSegments] = useState({});
  const [edit, setEdit] = useState(null);
  const canSet = (p) => ["super_admin", "admin", "sales_manager"].includes(user?.role) || (user?.role === "team_leader" && p.team_leader_id === user.id);
  const load = useCallback(() => { setData(null); api.get("/reports/employee-targets", { params: { month } }).then(({ data }) => setData(data)).catch(() => setData({})); }, [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/sales-targets/segments").then(({ data }) => setSegments(data));
    api.get("/users").then(({ data }) => setPeople(data.filter((u) => u.active !== false && u.role !== "super_admin" && (["super_admin", "admin", "sales_manager"].includes(user?.role) || u.id === user?.id || u.team_leader_id === user?.id))));
  }, [user]);
  return (
    <div className="mb-8" data-testid="targets-panel">
      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 font-bold"><Target className="h-5 w-5 text-primary" />Monthly targets</div>
        <label className="text-xs text-muted-foreground">Month<Input type="month" className="w-40 mt-1" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="targets-month" /></label>
      </div>
      {data && <Leaderboard people={people} data={data} />}
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{people.map((p) => { const segs = data[p.id] || []; return (
          <Card key={p.id} className="p-4" data-testid="target-card">
            <div className="flex justify-between gap-2"><div><div className="font-bold">{p.name}</div><div className="text-xs text-muted-foreground font-mono">{p.employee_id} · {titleCase(p.role)}</div></div>
              {canSet(p) && <Button size="sm" variant="outline" onClick={() => setEdit(p)} data-testid="target-set-btn">Set</Button>}</div>
            <div className="space-y-2.5 mt-3">{segs.length === 0 ? <p className="text-xs text-muted-foreground">No target for this month</p> : segs.map((s) => (
              <div key={s.business + s.segment} className="border-l-2 border-primary/30 pl-2"><div className="text-xs font-semibold">{s.segment} <span className="text-muted-foreground font-normal">· {s.business === "loan" ? "Loans" : "Insurance"}</span></div>
                <Bar label="Amount" done={s.done_amount} target={s.amount} money /><Bar label="Count" done={s.done_count} target={s.count} /></div>))}</div>
          </Card>); })}</div>)}
      {edit && <SetTargets person={edit} month={month} segments={segments} current={data?.[edit.id]} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
